import { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import MappedInPathDisplay from "./MappedInPathDisplay";
import { usePxToVw } from "../../hooks/usePxToVw";
import { useMappedInSettings } from "../../hooks/useMappedInSettings";

const MobileQRScanPage = () => {
  const { locationName, locationId, language } = useParams();
  const location = useLocation();
  const [locationData, setLocationData] = useState(null);
  const [mobileSettings, setMobileSettings] = useState(null);
  const [brandingSettings, setBrandingSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("map"); // 'map' or 'text'
  const [translatedScreenText, setTranslatedScreenText] = useState('');
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  });
  const [scanLogged, setScanLogged] = useState(false); // Track if scan was already logged
  const [mapUserId, setMapUserId] = useState(null);
  const { settings: mappedInConfig } = useMappedInSettings(mapUserId);

 

  // Get language from URL params or query params, default to 'en'
  const currentLanguage = language || new URLSearchParams(location.search).get('lang') || 'en';

  const hasLoggedRef = useRef(false);

  console.log("📱 MobileQRScanPage rendered with:", mobileSettings);
  console.log("📱 Language from URL:", currentLanguage);

  // Responsive design helper with better breakpoints
  const {pxToVw} = usePxToVw();

  const isMobile = windowSize.width <= 768;
  const isSmallMobile = windowSize.width <= 480;

  const normalizeTextForMobile = (text) => {
    if (!text) return '';

    return text
      // Remove SSML break tags
      .replace(/<break\s+time="[^"]*"\s*\/>/g, ' ')
      // Remove any other SSML tags
      .replace(/<[^>]*>/g, '')
      // Replace multiple spaces with single space
      .replace(/\s+/g, ' ')
      // Trim whitespace
      .trim();
  }

  // Helper function to get text in current language
  const getLanguageText = (field) => {
 
    if (!field) return '';

    // If field is a Map or object with language keys
    if (typeof field === 'object' && field !== null) {
      // Try current language first
      if (field[currentLanguage]) return field[currentLanguage];
      // Fallback to English
      if (field['en']) return field['en'];
      // If it's a Map, try to get values
      if (field instanceof Map) {
        return field.get(currentLanguage) || field.get('en') || '';
      }
    }

    // If it's a string, return as is
    if (typeof field === 'string') return field;

    return '';
  };

  // Helper function to get responsive font size from backend settings
  const getResponsiveFontSize = (backendSize) => {
    const size = parseInt(backendSize) || 16;
    return pxToVw(size);
  };

  // Helper function to get responsive spacing from backend settings
  const getResponsiveSpacing = (backendSpacing) => {
    const spacing = parseInt(backendSpacing) || 10;
    return pxToVw(spacing);
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (locationId) {
      fetchLocationData();
    }
  }, [locationId]);

  // Update document title when location data changes
  useEffect(() => {
    if (locationData) {
      // Helper function to get text in current language
      const getTranslatedName = (field) => {
        if (!field) return '';
        if (typeof field === 'object' && field !== null) {
          return field[currentLanguage] || field['en'] || '';
        }
        if (typeof field === 'string') return field;
        return '';
      };

      const translatedName = getTranslatedName(locationData.name) || locationName;
      document.title = `${translatedName} - Wayfinder`;
    }
  }, [locationData, currentLanguage, locationName]);

  const fetchLocationData = async () => {
    try {
      setLoading(true);

      // Fetch location data
      const locationResponse = await axios.get(
        `/api/wayfinder/locations/${locationId}`,
      );

      if (!locationResponse.data.success) {
        throw new Error("Location not found");
      }

      const location = locationResponse.data.data;
      setLocationData(location);
      setMapUserId(location.userId);

      // Priority: mobileScreenText (translate if needed) → textDirectionsMultiLang[lang] → textDirections
      let resolvedText = '';

      if (location.mobileScreenText) {
        // mobileScreenText is the primary source
        const rawText = location.mobileScreenText;
        // English — no translation needed, use as-is (preserves HTML formatting)
        if (!currentLanguage || currentLanguage === 'en') {
          resolvedText = rawText;
        } else {
          try {
            const translateRes = await axios.post('/api/wayfinder/translate-text', {
              text: rawText,
              targetLanguage: currentLanguage,
              sourceLanguage: 'en',
            });
            resolvedText = translateRes.data.success ? translateRes.data.translatedText : rawText;
          } catch (translateErr) {
            console.error('Translation failed, using original text:', translateErr);
            resolvedText = rawText;
          }
        }
      } else if (
        location.textDirectionsMultiLang &&
        typeof location.textDirectionsMultiLang === 'object' &&
        (location.textDirectionsMultiLang[currentLanguage] || location.textDirectionsMultiLang['en'])
      ) {
        // Fallback: already-translated multilang field — use directly
        resolvedText = location.textDirectionsMultiLang[currentLanguage] ||
          location.textDirectionsMultiLang['en'];
      } else if (location.textDirections) {
        // Last resort: plain textDirections
        const rawText = location.textDirections;
        if (currentLanguage && currentLanguage !== 'en') {
          try {
            const translateRes = await axios.post('/api/wayfinder/translate-text', {
              text: rawText,
              targetLanguage: currentLanguage,
              sourceLanguage: 'en',
            });
            resolvedText = translateRes.data.success ? translateRes.data.translatedText : rawText;
          } catch (translateErr) {
            resolvedText = rawText;
          }
        } else {
          resolvedText = rawText;
        }
      }

      setTranslatedScreenText(resolvedText);

      const clientUrl = locationResponse.data.clientUrl || null;

      // Log the QR code scan only once
      // if (!scanLogged) {
      //   try {
      //     console.log("📝 Logging QR scan:", {
      //       locationId,
      //       userId: location.userId,
      //       language: currentLanguage,
      //     });

      //     const logResponse = await axios.post(
      //       `/api/wayfinder/log-qr-scan/${location.userId}`,
      //       {
      //         locationId: locationId,
      //         language: currentLanguage, // Include language for tracking
      //       },
      //     );

      //     if (logResponse.data.success) {
      //       console.log(
      //         "✅ QR scan logged:",
      //         logResponse.data.duplicate ? "(duplicate)" : "(new)",
      //         "Language:",
      //         currentLanguage,
      //       );
      //       setScanLogged(true); // Mark as logged to prevent duplicate calls
      //     }
      //   } catch (logError) {
      //     console.error(
      //       "❌ Error logging QR scan:",
      //       logError.response?.data || logError.message,
      //     );
      //     // Still mark as logged to prevent retries
      //     setScanLogged(true);
      //   }
      // }

      if (!hasLoggedRef.current) {
        try {
          hasLoggedRef.current = true;

          console.log("📝 Logging QR scan for locationId:", location);

          const logResponse = await axios.post("/api/log/location-qr-scan", {
            qr_url: window.location.href,
            locationId: locationId,
            scannedLocationName: getLanguageText(location.name) || locationName || "Unknown Location",
            client_url: clientUrl || "unknown",
            buttonType: "location",
            categoryId: location.categoryId || null,
            subcategoryId: location.subcategoryId || null,
            language: currentLanguage || 'en',
          });

          console.log("✅ Location QR scan logged successfully");
        } catch (err) {
          console.error("❌ Error logging QR scan:", err);
        }
      }


      // Fetch mobile screen settings
      const settingsResponse = await axios.get(
        `/api/wayfinder/mobile-screen-settings/${location.userId}`,
      );

      if (settingsResponse.data.success) {
        const globalSettings = settingsResponse.data.data;

        // Get translated text directions based on current language
        let translatedText = '';

        // First, try to get from textDirectionsMultiLang
        if (location.textDirectionsMultiLang && typeof location.textDirectionsMultiLang === 'object') {
          translatedText = location.textDirectionsMultiLang[currentLanguage] ||
            location.textDirectionsMultiLang['en'] || '';
        }

        // If not found, fall back to mobileScreenText or textDirections
        if (!translatedText) {
          translatedText = location.mobileScreenText || location.textDirections || '';
        }

        const mergedSettings = {
          ...globalSettings,
          avatarText: translatedText || globalSettings.avatarText || "",
        };
        setMobileSettings(mergedSettings);
      } else {
        // Get translated text for fallback case
        let translatedText = '';
        if (location.textDirectionsMultiLang && typeof location.textDirectionsMultiLang === 'object') {
          translatedText = location.textDirectionsMultiLang[currentLanguage] ||
            location.textDirectionsMultiLang['en'] || '';
        }
        if (!translatedText) {
          translatedText = location.mobileScreenText || location.textDirections || '';
        }

        setMobileSettings({
          avatarText: translatedText,
        });
      }

      // Fetch branding settings for logoBeforeMap
      try {
        console.log("📝 Fetching branding settings for userId:", location.userId);
        const brandingResponse = await axios.get(
          `/api/wayfinder/branding-settings/${location.userId}`,
        );

        if (brandingResponse.data.success) {
          setBrandingSettings(brandingResponse.data.data);
          console.log("✅ Branding settings loaded:", brandingResponse.data.data?.logoBeforeMap);
        } else {
          console.log("⚠️ Branding settings not found, using defaults");
          setBrandingSettings(null);
        }
      } catch (brandingError) {
        console.error("❌ Error fetching branding settings:", brandingError);
        setBrandingSettings(null);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.message || "Failed to load location data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: mobileSettings?.header?.backgroundColor || "#f5f5f5",
          fontFamily: mobileSettings?.fontFamily || "Arial, sans-serif",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: getResponsiveFontSize(24),
              marginBottom: getResponsiveSpacing(10),
              color: mobileSettings?.header?.titleColor || "#333",
            }}
          >
            Loading...
          </div>
          <div
            style={{
              fontSize: getResponsiveFontSize(16),
              color: mobileSettings?.header?.locationTextColor || "#666",
            }}
          >
            {locationName}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: mobileSettings?.header?.backgroundColor || "#f5f5f5",
          fontFamily: mobileSettings?.fontFamily || "Arial, sans-serif",
        }}
      >
        <div style={{ textAlign: "center", padding: getResponsiveSpacing(20) }}>
          <div
            style={{
              fontSize: getResponsiveFontSize(24),
              marginBottom: getResponsiveSpacing(10),
              color: "#e74c3c",
            }}
          >
            Error
          </div>
          <div
            style={{
              fontSize: getResponsiveFontSize(16),
              color: mobileSettings?.header?.locationTextColor || "#666",
            }}
          >
            {error}
          </div>
        </div>
      </div>
    );
  }

  const headerStyle = {
    backgroundColor: mobileSettings?.header?.backgroundColor || "#ffffff",
    padding: pxToVw(mobileSettings?.headerPadding || 10),
    marginBottom: pxToVw(mobileSettings?.headerMarginBottom || 10),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    position: "sticky",
    top: 0,
    zIndex: 1000,
  };

  const logoStyle = {
    width: pxToVw(160),
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
  };

  const centerContentStyle = {
    flex: 1,
    textAlign: "center",
  };

  const titleStyle = {
    fontFamily: mobileSettings?.fontFamily || "Arial, sans-serif",
    fontWeight: mobileSettings?.header?.fontWeight || 600,
    color: mobileSettings?.header?.titleColor || "#000000",
    fontSize: pxToVw(
      mobileSettings?.header?.titleFontSize || 18,
    ),
    marginBottom: pxToVw(
      mobileSettings?.header?.marginBottom || 4,
    ),
    margin: 0,
  };

  const locationTextStyle = {
    color: mobileSettings?.header?.locationTextColor || "#000000",
    fontSize: pxToVw(
      mobileSettings?.header?.locationTextFontSize || 14,
    ),
    margin: 0,
    fontFamily: mobileSettings?.fontFamily || "Arial, sans-serif",
  };

  const buttonContainerStyle = {
    display: "flex",
    justifyContent: "center",
    gap: pxToVw(parseInt(mobileSettings?.buttons?.gap ?? 10)),
    padding: pxToVw(mobileSettings?.headerPadding || 20),
    flexWrap: "wrap",
  };

  const getButtonStyle = (buttonType, isActive) => {
    const buttonSettings = mobileSettings?.buttons?.[buttonType] || {};
    return {
      height: pxToVw(buttonSettings.buttonHeight || 40),
      width: pxToVw(buttonSettings.buttonWidth || 100),
      backgroundColor: isActive
        ? buttonSettings.activeBackgroundColor || "#115293"
        : buttonSettings.backgroundColor || "#1976d2",
      color: isActive
        ? buttonSettings.buttonActiveTextColor || "#ffffff"
        : buttonSettings.buttonTextColor || "#ffffff",
      border: `${pxToVw(buttonSettings.borderWidth || 1)} solid ${buttonSettings.borderColor || "#000000"}`,
      borderRadius: pxToVw(buttonSettings.borderRadius || 0),
      fontSize: pxToVw(buttonSettings.buttonFontSize || 14),
      fontFamily: mobileSettings?.fontFamily || "Arial, sans-serif",
      padding: pxToVw(buttonSettings.buttonPadding || 10),
      cursor: "pointer",
      transition: "all 0.3s ease",
      fontWeight: "500",
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    };
  };

  const contentStyle = {
    flex: 1,
    overflow: "auto",
    position: "relative",
    backgroundColor: "#f8f9fa", // Light grey background for content area only
  };

  const mapContainerStyle = {
    width: "100%",
    height: "100%",
    minHeight: pxToVw(isMobile ? 600 : 700),
    position: "relative",
    backgroundColor: "#ffffff",
    display: activeTab === "map" ? "block" : "none",
    overflow: "hidden",
    padding: pxToVw(mobileSettings?.mapPagePadding || 20),
  };

  const mapWrapperStyle = {
    width: "100%",
    height: "100%",
    border: `3px solid #8d8d8e`,
    borderRadius: pxToVw(12),
    overflow: "hidden",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  };

  const textContentStyle = {
    fontFamily: mobileSettings?.textSettings?.fontFamily || mobileSettings?.fontFamily || "Arial, sans-serif",
    fontSize: pxToVw(mobileSettings?.textSettings?.fontSize || 16),
    lineHeight: 1.6,
    color: "#333333",
    display: activeTab === "text" ? "block" : "none",
    padding: pxToVw(mobileSettings?.textPagePadding || 20),
    height: "100%",
    overflow: "auto",
    backgroundColor: "#ffffff",
    wordWrap: "break-word",
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        fontFamily: mobileSettings?.fontFamily || "Arial, sans-serif",
        backgroundColor: "#ffffff", // White background for main container
      }}
    >
      {/* Header */}
      <div style={headerStyle}>
        {/* Left: Logo */}
        <div style={logoStyle}>
          {mobileSettings?.headerLogo?.enabled &&
            mobileSettings?.headerLogo?.fileUrl && (
              <img
                src={mobileSettings.headerLogo.fileUrl}
                alt="Logo"
                style={{
                  width: `${mobileSettings.headerLogo.width}%`,
                  maxWidth: isMobile ? "100px" : "140px",
                  height: "auto",
                }}
              />
            )}
        </div>

        {/* Center: Content */}
        <div style={centerContentStyle}>
          <h1 style={titleStyle}>
            {mobileSettings?.header?.title || "WAYFINDER"}
          </h1>
          <p style={locationTextStyle}>{getLanguageText(locationData?.name) || locationName}</p>
        </div>

        {/* Right: Spacer (balances logo width) */}
        <div style={logoStyle} />
      </div>

      {/* Tab Buttons */}
      <div style={buttonContainerStyle}>
        <button
          style={getButtonStyle("mapButton", activeTab === "map")}
          onClick={() => setActiveTab("map")}
        >
          {mobileSettings?.buttons?.mapButton?.text || "Map"}
        </button>

        <button
          style={getButtonStyle("textButton", activeTab === "text")}
          onClick={() => setActiveTab("text")}
        >
          {mobileSettings?.buttons?.textButton?.text || "Text"}
        </button>
      </div>

      {/* Content Area */}
      <div style={contentStyle}>
        {/* Map Container */}
        <div style={mapContainerStyle}>
          {locationData?.spaceName || locationData?.externalId ? (
            <div style={mapWrapperStyle} id="mappedin-map-mobile">
              <MappedInPathDisplay
                spaceName={locationData.spaceName}
                externalId={locationData.externalId}
                locationName={getLanguageText(locationData.name)}
                brandColor={
                  mobileSettings?.buttons?.mapButton?.backgroundColor ||
                  "#1976d2"
                }
                logoBeforeMap={brandingSettings?.logoBeforeMap}
                onError={() => console.log("Error loading map")}
                isMobile={isMobile}
                userId={locationData.userId}
                mapLink={mappedInConfig?.mapLink || ''}
                currentLanguage={currentLanguage}
              />
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                color: "#666",
                fontSize: getResponsiveFontSize(16),
                textAlign: "center",
                border: `2px dashed #ccc`,
                borderRadius: getResponsiveSpacing(12),
                backgroundColor: "#f9f9f9",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: getResponsiveFontSize(48),
                    marginBottom: getResponsiveSpacing(20),
                  }}
                >
                  🗺️
                </div>
                <div>Map not available for this location</div>
                <div
                  style={{
                    fontSize: getResponsiveFontSize(14),
                    marginTop: getResponsiveSpacing(10),
                    opacity: 0.7,
                  }}
                >
                  Please contact support for assistance
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={textContentStyle} className="text-content-container">
          {translatedScreenText ? (
            <div
              dangerouslySetInnerHTML={{ __html: translatedScreenText }}
            />
          ) : (
            <div
              style={{
                textAlign: "center",
                color: "#666",
                fontSize: getResponsiveFontSize(16),
                padding: getResponsiveSpacing(40),
                backgroundColor: "#f8f9fa",
                borderRadius: getResponsiveSpacing(8),
              }}
            >
              <div
                style={{
                  fontSize: pxToVw(mobileSettings?.textSettings?.fontSize || 16),
                  marginBottom: pxToVw(20),
                  fontFamily: mobileSettings?.textSettings?.fontFamily || "Arial, sans-serif",
                }}
              >
                No text directions available
              </div>
            </div>
          )}
        </div>

        {/* CSS to ensure list numbers and formatting display properly */}
        <style>{`
          .text-content-container ol {
            padding-left: 20px !important;
            margin: 10px 0 !important;
            list-style-type: decimal !important;
            list-style-position: outside !important;
          }
          .text-content-container ul {
            padding-left: 20px !important;
            margin: 10px 0 !important;
            list-style-type: disc !important;
            list-style-position: outside !important;
          }
          .text-content-container li {
            margin-bottom: 8px !important;
            line-height: 1.6 !important;
            display: list-item !important;
          }
          .text-content-container p {
            margin: 10px 0 !important;
          }
          .text-content-container h1,
          .text-content-container h2,
          .text-content-container h3,
          .text-content-container h4,
          .text-content-container h5,
          .text-content-container h6 {
            margin: 15px 0 10px 0 !important;
            font-weight: bold !important;
          }
          .text-content-container pre {
            white-space: pre-wrap !important;
            word-wrap: break-word !important;
            background-color: #f5f5f5 !important;
            padding: 10px !important;
            border-radius: 4px !important;
            overflow-x: auto !important;
          }
          .text-content-container code {
            white-space: pre-wrap !important;
            word-wrap: break-word !important;
          }
        `}</style>
      </div>
    </div>
  );
};

export default MobileQRScanPage;
